import { cn } from '@wellesley/ui'
import {
  Maximize,
  PictureInPicture2,
  RectangleHorizontal,
  Volume2,
  VolumeX,
} from 'lucide-react'
import {
  ChangeEvent,
  FC,
  MouseEvent,
  RefObject,
  useEffect,
  useState,
} from 'react'
import formatTime from 'helpers/time/formatTime'
import Timeline from '../Timeline'
import PlayToggler from '../PlayToggler'
import useHover from 'hooks/useHover'
import { VideoProps } from './types'

type VideoPanelProps = Pick<VideoProps, 'isSmallPreview' | 'onClosePreview'> & {
  muted: boolean
  volume: number
  progress: number
  isPlaying: boolean
  currentTime: number
  containerRef: RefObject<HTMLElement>
  isBigPreview: boolean
  videoDuration: number
  togglePlay: () => void
  onFullscreen: () => void
  toggleMute: (e: MouseEvent<HTMLDivElement>) => void
  handleSeek: (event: React.ChangeEvent<HTMLInputElement>) => void
  onVolumeChange: (e: ChangeEvent<HTMLInputElement>) => void
}

const VideoPanel: FC<VideoPanelProps> = ({
  muted,
  volume: initialVolume,
  isPlaying,
  currentTime,
  isBigPreview,
  containerRef,
  videoDuration,
  isSmallPreview,
  togglePlay,
  toggleMute,
  onFullscreen,
  onClosePreview,
  onVolumeChange,
  ...timelineProps
}) => {
  const [volume, setVolume] = useState(initialVolume)
  const [hoverRef, isHovered] = useHover<HTMLDivElement>()

  useEffect(() => {
    setVolume(initialVolume)
  }, [initialVolume])

  const handleVolumeChange = (e: ChangeEvent<HTMLInputElement>) => {
    const newVolume = Number(e.target.value) / 100
    setVolume(newVolume)
    onVolumeChange(e)
  }

  const volumeValue = volume * 100

  const toggleFullscreen = async () => {
    const video = containerRef.current?.querySelector('video')

    if (containerRef.current && video) {
      if (document.fullscreenElement) {
        await document.exitFullscreen()
        video.style.removeProperty('max-height')
      } else {
        await containerRef.current.requestFullscreen()
        video.style.maxHeight = '100%'
      }
    }
  }

  const handlePlaybarClick = (e: MouseEvent<HTMLDivElement>) => {
    const isPreviewClick =
      (e.target as Element).parentElement?.getAttribute('data-testid') ===
      'preview'

    !isPreviewClick && e.stopPropagation()
  }

  return (
    <div
      data-testid="video-panel"
      onClick={handlePlaybarClick}
      className={cn(
        'px-xxs pb-xs pt-xxs bg-alpha-black-30 absolute bottom-0 left-0 w-full rounded-[3px] rounded-t-none backdrop-blur-[3px]',
      )}
    >
      <Timeline {...timelineProps} />
      <div className="mt-xxs flex items-center justify-between text-sm font-semibold text-white">
        <PlayToggler position="bottom-left" {...{ isPlaying, togglePlay }} />
        <div className="gap-xxs flex items-center">
          {videoDuration !== 0 && !isSmallPreview && (
            <span>{formatTime(currentTime)}</span>
          )}
          <div ref={hoverRef} className="relative cursor-pointer">
            <div
              onClick={toggleMute}
              className={cn(
                'before:absolute before:bottom-full before:left-0  before:size-[22px] before:content-[""]',
                { 'before:z-[1]': isHovered },
              )}
            >
              {muted ? <VolumeX size={20} /> : <Volume2 size={20} />}
            </div>
            {isHovered && (
              <div className="absolute bottom-[calc(100%+22px)] left-full z-10">
                <div className="p-xxs bg-alpha-black-30 relative origin-bottom-left -rotate-90 transform backdrop-blur-[3px]">
                  <div className="bg-alpha-white-60 h-0.5 w-16 rounded-[3px]">
                    <div
                      className="slider-thumb [&:after]:-right-xxxs relative h-full rounded-[3px] bg-white transition-all after:z-[1] after:translate-y-[3px]"
                      style={{ width: `${volumeValue}%` }}
                    />
                  </div>
                  <input
                    type="range"
                    min="0"
                    max="100"
                    value={volumeValue}
                    onChange={handleVolumeChange}
                    className="absolute left-0 top-0 z-[1] h-[18px] w-20 cursor-pointer opacity-0"
                  />
                </div>
              </div>
            )}
          </div>
          <div
            data-testid="preview"
            onClick={isBigPreview ? onClosePreview : undefined}
          >
            {isBigPreview ? (
              <PictureInPicture2 className="cursor-pointer" size={20} />
            ) : (
              <RectangleHorizontal className="cursor-pointer" size={20} />
            )}
          </div>
          {isBigPreview && (
            <Maximize
              size={20}
              data-testid="fullscreen-button"
              className="cursor-pointer"
              onClick={
                onFullscreen
                  ? () => void onFullscreen()
                  : () => void toggleFullscreen()
              }
            />
          )}
        </div>
      </div>
    </div>
  )
}

export default VideoPanel
